import {Component} from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';

import {isMobile} from '-/util/util';
import {important, inputPlaceholder, lighten, rem} from '-/styles/utils';

const styles = theme => ({
    root: {
        display: 'table',
        margin: '0 auto',

        '& span, & input[type="tel"]': {
            padding: 0,
            display: 'inline-block',
            border: 'none',
            fontFamily: theme.fontFamily,
            fontSize: rem(31),
            fontWeight: 'normal',
            lineHeight: rem(44),
            letterSpacing: rem(3),
            color: '#aaa',
            backgroundColor: 'transparent',
            ...inputPlaceholder({
                color: lighten('#aaa', .2),
                fontSize: rem(31)
            }),
            '&:focus, &:active': {
                outline: 'none',
                border: 'none',
                boxShadow: 'none'
            }
        }
    },
    month: {
        width: important(55)
    },
    day: {
        width: important(50)
    },
    year: {
        width: important(90)
    }
});

class AuthDateInput extends Component {
    static defaultProps = {
        includeDay: true,
        onChange: () => {},
        onEnterKey: () => {}
    };

    static propTypes = {
        includeDay: PropTypes.bool,
        onChange: PropTypes.func,
        onEnterKey: PropTypes.func
    };

    constructor(props) {
        super(props);

        this.handleClick = this.handleClick.bind(this);
        this.handleMonthChange = this.handleMonthChange.bind(this);
        this.handleDayChange = this.handleDayChange.bind(this);
        this.handleInputFocus = this.handleInputFocus.bind(this);
        this.handleMonthAndDayBlur = this.handleMonthAndDayBlur.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.onlyNumbers = this.onlyNumbers.bind(this);
    }

    getValues() {
        const {refs} = this;
        const {month, day, year} = refs;
        const {includeDay} = this.props;

        if (!month.value || month.value.length !== 2 ||
            (includeDay && day && (!day.value || day.value.length !== 2)) ||
            !year.value || year.value.length !== 4)
        {
            return null;
        }

        return {
            day,
            month,
            year
        };
    }

    get rawValue() {
        const values = this.getValues();

        if (!values) {
            return {};
        }

        const {day, month, year} = values;

        return {
            day: day.value,
            month: month.value,
            year: year.value
        };
    }

    get value() {
        const values = this.getValues();

        if (!values) {
            return null;
        }

        const {day, month, year} = values;
        const {includeDay} = this.props;

        const value = `${year.value}-${month.value}${includeDay ? '-' + day.value : ''}`;
        return new Date(value);
    }

    componentDidMount() {
        if (!isMobile()) {
            this.refs.month.focus();
            this.refs.month.select();
        }
    }

    handleClick() {
        this.refs.month.select();
        this.refs.month.focus();
    }

    onlyNumbers(e) {
        if (e.target.value) {
            e.target.value = e.target.value.replace(/[^0-9\.]/g, '');
        }
    }

    handleMonthChange(e) {
        if (e.target.value.length === 2) {
            const ref = this.props.includeDay ? this.refs.day : this.refs.year;
            ref.select();
            ref.focus();
        }
    }

    handleDayChange(e) {
        if (e.target.value.length === 2) {
            this.refs.year.select();
            this.refs.year.focus();
        }
    }

    handleInputFocus(e) {
        const {month, day, year} = this.refs;
        const {includeDay} = this.props;

        if (!e.target.value) {
            if (month.value.length < 2) {
                month.focus();
                month.select();
            } else if (includeDay && new String(day.value).length < 2) {
                day.focus();
                day.select();
            }
        }
        else {
            e.target.select();
        }
    }

    handleMonthAndDayBlur(e) {
        this.onlyNumbers(e);

        if (parseInt(e.target.value) > parseInt(e.target.max)) {
            e.target.value = e.target.max;
        }
        if (parseInt(e.target.value) < parseInt(e.target.min)) {
            e.target.value = e.target.min;
        }
        if (e.target.value.length === 1) {
            e.target.value = '0' + e.target.value;
        }
    }

    handleKeyDown(e) {
        // enter key
        if (e.keyCode === 13) {
            this.props.onEnterKey();
        }
    }

    render() {
        const {includeDay, classes = {}} = this.props;
        return (
            <div className={classes.root} onKeyDown={this.handleKeyDown}>
                <input
                    ref="month"
                    placeholder="MM"
                    className={classes.month}
                    type="tel"
                    maxLength="2"
                    min="1"
                    max="12"
                    onBlur={this.handleMonthAndDayBlur}
                    onFocus={this.handleInputFocus}
                    onChange={this.handleMonthChange} />
                <span onClick={this.handleClick}>&nbsp;</span>
                {includeDay ? (
                    <input
                        ref="day"
                        placeholder="DD"
                        className={classes.day}
                        type="tel"
                        maxLength="2"
                        min="1"
                        max="31"
                        onBlur={this.handleMonthAndDayBlur}
                        onFocus={this.handleInputFocus}
                        onChange={this.handleDayChange} />
                ) : null}
                {includeDay ? <span onClick={this.handleClick}>&nbsp;</span> : null}
                <input
                    ref="year"
                    placeholder="YYYY"
                    className={classes.year}
                    type="tel"
                    maxLength="4"
                    onFocus={this.handleInputFocus}
                    onChange={this.onlyNumbers} />
            </div>
        );
    }
}

export default injectSheet(styles)(AuthDateInput);
